import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MdInfo } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ActiveProgram } from "../../../utils/hooks/dashboard/useExecutiveSummaryData";
import { FiPlay, FiClock, FiCheckCircle, FiExternalLink } from "react-icons/fi";

interface ActiveProgramsTableProps {
  programs: ActiveProgram[];
  isLoading?: boolean;
}

const getStatusBadge = (status: string) => {
  const s = status.toLowerCase();
  if (s.includes("active") || s.includes("progress")) {
    return {
      className: "bg-blue-50 text-blue-600 border-none",
      icon: <FiPlay className="h-4 w-4 fill-blue-600/10" />
    };
  }
  if (s.includes("scheduled")) {
    return {
      className: "bg-amber-50 text-amber-600 border-none",
      icon: <FiClock className="h-4 w-4" />
    };
  }
  if (s.includes("completed")) {
    return {
      className: "bg-emerald-50 text-emerald-600 border-none",
      icon: <FiCheckCircle className="h-4 w-4" />
    };
  }
  return {
    className: "bg-gray-50 text-gray-600 border-none",
    icon: null
  };
};

const ActiveProgramsTable: React.FC<ActiveProgramsTableProps> = ({ programs, isLoading }) => {
  const navigate = useNavigate();
  const [showInfo, setShowInfo] = useState(false);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-6 w-1/4 bg-gray-100 rounded"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 bg-gray-50 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-full"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-slate-800">Active Programs</h3>
          <div className="relative">
            <MdInfo 
              className="h-4 w-4 text-slate-300 hover:text-blue-500 cursor-help transition-colors"
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            />
            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute left-0 top-full mt-2 z-50 w-64 bg-[#010D2C] text-white p-3 rounded-xl text-[11px] font-medium shadow-xl pointer-events-none leading-relaxed"
                >
                  A list of your current security testing programs, showing their current status, focus areas, and scheduled completion dates.
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <button 
          onClick={() => navigate("/dashboard/programs")}
          className="flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors"
        >
          View all
          <FiExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="overflow-x-auto px-2">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F3F4F6]">
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 w-1/3">Project</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500">Type</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500">Status</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500">Due Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {programs.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                  No active programs found.
                </td>
              </tr>
            ) : (
              programs.map((program) => {
                const badge = getStatusBadge(program.status);
                return (
                  <tr key={program.program_id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-5">
                      <span className="font-medium text-slate-800 text-sm">{program.name}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-slate-400 font-normal text-xs">
                        {program.target_types.length > 0 ? program.target_types.join(", ") : "General Security"}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-[11px] uppercase tracking-wide ${badge.className}`}>
                        {badge.icon}
                        {program.status}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-slate-400 font-normal text-xs">
                        {program.due_date ? new Date(program.due_date).toISOString().split('T')[0] : "—"}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="pb-4" />
    </motion.div>
  );
};

export default React.memo(ActiveProgramsTable);

